import { useMemo } from 'react';

/**
 * 枚举选项接口（与后端返回的数据结构一致）
 */
export interface EnumOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  color?: string;
  order?: number;
  default?: boolean;
}

/**
 * 枚举定义接口（与后端返回的数据结构一致）
 */
export interface EnumDefinition {
  fieldName: string;
  label: string;
  options: EnumOption[];
}

/**
 * 枚举场景配置接口（与后端返回的数据结构一致）
 */
export interface EnumSceneConfig {
  scene: 'enum';
  enums: EnumDefinition[];
}

/**
 * useEnums Hook 配置
 */
export interface UseEnumsConfig {
  /** tRPC React hooks 实例（需含 meta.getMeta） */
  trpc: {
    meta: {
      getMeta?: {
        useQuery: (input: { source: string; scene: 'enum' }) => {
          data: EnumSceneConfig | undefined;
          isLoading: boolean;
          error: any;
        };
      };
    };
    [key: string]: any;
  };
  /** 资源表名列表（defineTable 首参，如 ['app', 'spaces']），不提供则默认 app + spaces */
  modules?: string[];
}

/**
 * 合并多个模块的枚举数据
 */
function mergeEnumData(
  enumConfigs: (EnumSceneConfig | undefined)[]
): Record<string, EnumDefinition> {
  const result: Record<string, EnumDefinition> = {};

  enumConfigs.forEach((config) => {
    if (!config?.enums) return;

    config.enums.forEach((enumDef) => {
      // 使用 fieldName 作为 key，直接存储枚举定义
      result[enumDef.fieldName] = enumDef;
    });
  });

  return result;
}

/**
 * 获取所有枚举定义
 *
 * @param config - Hook 配置，包含 trpc 实例和模块列表
 * @returns 所有枚举的定义和便捷方法
 */
export function useEnums(config: UseEnumsConfig) {
  const { trpc, modules } = config;

  // 确定要查询的模块列表
  const modulesToQuery = modules || ['app', 'spaces'];

  // 从 meta 路由按表名拉取 enum 场景
  const enumQueries = modulesToQuery.map((source) =>
    trpc.meta.getMeta?.useQuery({ source, scene: 'enum' })
  );

  // 合并加载状态和错误
  const isLoading = enumQueries.some((query) => query?.isLoading) || false;
  const error = enumQueries.find((query) => query?.error)?.error;

  // 合并所有枚举数据
  const enumDataList = enumQueries.map((query) => query?.data).filter((config): config is EnumSceneConfig => !!config);
  const enums = useMemo(() => {
    return mergeEnumData(enumDataList);
  }, [enumDataList]);

  /**
   * 根据模块名和字段名获取枚举定义
   * @param moduleName 模块名（如 'apps'）
   * @param fieldName 字段名（如 'type', 'status'）
   * @returns 枚举定义，如果不存在则返回 undefined
   */
  const getEnum = (
    moduleName: string,
    fieldName: string
  ): EnumDefinition | undefined => {
    // 尝试直接使用 fieldName
    if (enums[fieldName]) {
      return enums[fieldName];
    }
    // 如果找不到，返回 undefined
    return undefined;
  };

  /**
   * 获取枚举标签
   * @param moduleName 模块名（如 'apps'）
   * @param fieldName 字段名（如 'type', 'status'）
   * @param value 枚举值
   * @returns 枚举标签，如果不存在则返回原值或 '-'
   */
  const getLabel = (
    moduleName: string,
    fieldName: string,
    value: string | null | undefined
  ): string => {
    if (!value) return '-';
    const enumDef = getEnum(moduleName, fieldName);
    if (!enumDef) return value;
    const option = enumDef.options.find((opt) => opt.value === value);
    return option?.label || value;
  };

  /**
   * 获取枚举选项
   * @param moduleName 模块名（如 'apps'）
   * @param fieldName 字段名（如 'type', 'status'）
   * @returns 枚举选项数组，如果不存在则返回空数组
   */
  const getOptions = (
    moduleName: string,
    fieldName: string
  ): EnumOption[] => {
    const enumDef = getEnum(moduleName, fieldName);
    return enumDef?.options || [];
  };

  /**
   * 获取枚举选项（按 order 排序）
   * @param moduleName 模块名（如 'apps'）
   * @param fieldName 字段名（如 'type', 'status'）
   * @returns 排序后的枚举选项数组
   */
  const getSortedOptions = (
    moduleName: string,
    fieldName: string
  ): EnumOption[] => {
    const options = getOptions(moduleName, fieldName);
    return [...options].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  };

  /**
   * 获取枚举元数据（单个选项）
   * @param moduleName 模块名（如 'apps'）
   * @param fieldName 字段名（如 'type', 'status'）
   * @param value 枚举值
   * @returns 枚举选项，如果不存在则返回 undefined
   */
  const getOption = (
    moduleName: string,
    fieldName: string,
    value: string | null | undefined
  ): EnumOption | undefined => {
    if (!value) return undefined;
    const enumDef = getEnum(moduleName, fieldName);
    return enumDef?.options.find((opt) => opt.value === value);
  };

  return {
    // 原始枚举数据（按 fieldName 索引）
    enums,
    isLoading,
    error,
    // 便捷方法
    getEnum,
    getLabel,
    getOptions,
    getSortedOptions,
    getOption,
  };
}
